import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { useApp } from '../context/AppContext';
import StateFallback from '../components/StateFallback';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- Pet Escape Early Warning (frontend-only hackathon mock) ---
const DESIGNATED_EXIT = 'Back gate';
const EXIT_OPTIONS = ['Back gate', 'Front door', 'Fence', 'Garage door'];
const ESCAPE_EVENTS = [
  { time: '7:12 PM', text: 'Activity detected outdoors', type: 'outdoors', minute: 432 },
  { time: '7:14 PM', text: 'Pet near back gate', type: 'near_exit', minute: 434 },
  { time: '7:17 PM', text: 'Pet near back gate', type: 'near_exit', minute: 437 },
  { time: '7:19 PM', text: 'Pet near back gate', type: 'near_exit', minute: 439 },
];

// Transparent rule (UNCHANGED): at least 3 near_exit events whose first-to-third
// span is <= 10 minutes. Values are minutes; the same rule is applied to both the
// deterministic sample events and to live Ring observation timestamps.
const escapeRuleMet = (minutes) =>
  minutes.length >= 3 &&
  Math.max(...minutes) - Math.min(...minutes) <= 10;

// Deterministic sample demo evaluation (kept exactly as before).
const SAMPLE_NEAR_EXIT_MINUTES = ESCAPE_EVENTS.filter(
  (e) => e.type === 'near_exit'
).map((e) => e.minute);
const SAMPLE_ESCAPE_ALERT = escapeRuleMet(SAMPLE_NEAR_EXIT_MINUTES);

const Home = () => {
  const navigate = useNavigate();
  const { pet, attention, memories, loading, error, reload } = useApp();

  // Locally selectable designated exit (React state only, no persistence).
  const [exit, setExit] = useState(DESIGNATED_EXIT);

  // Live Ring observations (source-neutral). Reuses the existing read-only
  // GET /api/observations. Only real, stored Ring motion_detected observations
  // reach this collection; the sandbox Playground endpoint never persists, so
  // its on_demand / non_demand history records can NEVER appear here or count
  // toward the escape warning.
  const [ringActivity, setRingActivity] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/observations`);
        if (!alive || !Array.isArray(r.data)) return;
        // Prototype assumption: the selected Ring camera/device monitors the
        // user-designated exit area, so a Ring motion_detected observation is
        // treated as a near_exit signal. No maps/zones/CV are used, and we do
        // NOT claim Ring identified a specific pet.
        const ring = r.data
          .filter((o) => o.source === 'Ring' && o.observedAt)
          .map((o) => ({
            id: o.id,
            time: o.time,
            minute: Math.floor(new Date(o.observedAt).getTime() / 60000),
          }))
          .filter((o) => Number.isFinite(o.minute));
        setRingActivity(ring);
      } catch (e) {
        // Non-fatal: escape prototype still runs on deterministic sample data.
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const active = memories.find((m) => m.status !== 'dismissed');
  const isConfirmed = active && active.status === 'confirmed';

  // Same unchanged rule, evaluated over actual Ring observation timestamps.
  const ringEscapeAlert = escapeRuleMet(ringActivity.map((o) => o.minute));
  // The alert appears if the deterministic sample data OR live Ring activity
  // satisfies the (unchanged) rule.
  const escapeAlert = SAMPLE_ESCAPE_ALERT || ringEscapeAlert;

  if (loading) {
    return <StateFallback variant="loading" />;
  }
  if (error || !pet) {
    return (
      <StateFallback
        variant="error"
        message="We couldn't load Milo's profile."
        onRetry={reload}
      />
    );
  }

  return (
    <div className="px-6 pt-2 pb-16 lp-enter">
      {/* Header */}
      <div className="flex items-center justify-between mb-7">
        <div className="flex items-center gap-2">
          <img
            src="/lashachi-pet-logo.png"
            alt="Lashachi Pet"
            className="w-[30px] h-[30px] rounded-[7px] object-contain"
            data-testid="home-brand-logo"
          />
          <span
            className="font-serif-lp text-[19px]"
            style={{ color: 'var(--lp-cream)' }}
          >
            Lashachi <span style={{ color: 'var(--lp-body)' }}>Pet</span>
          </span>
        </div>
        <button
          onClick={() => navigate('/settings')}
          className="text-[16px] lp-pressable"
          style={{ color: 'var(--lp-cream)' }}
        >
          Settings
        </button>
      </div>

      {/* Pet profile */}
      <div className="flex items-center gap-4 mb-7">
        <img
          src={pet.avatar}
          alt={pet.name}
          className="w-[68px] h-[68px] rounded-full object-cover"
          style={{ boxShadow: '0 0 0 3px rgba(255,255,255,0.14)' }}
        />
        <div>
          <h2
            className="font-serif-lp text-[24px] leading-tight"
            style={{ color: 'var(--lp-cream)' }}
          >
            {pet.name}
          </h2>
          <p className="text-[14.5px]" style={{ color: 'var(--lp-body)' }}>
            {pet.breed} · {pet.profileTag}
          </p>
        </div>
      </div>

      {/* Measurements */}
      <div className="flex justify-between mb-7 pr-2">
        {[
          { label: 'Neck', value: pet.measurements.neck },
          { label: 'Chest', value: pet.measurements.chest },
          { label: 'Back', value: pet.measurements.back },
        ].map((m) => (
          <div key={m.label}>
            <p
              className="text-[12.5px] mb-0.5"
              style={{ color: 'var(--lp-muted)' }}
            >
              {m.label}
            </p>
            <p
              className="text-[17px] font-semibold"
              style={{ color: 'var(--lp-cream)' }}
            >
              {m.value}
            </p>
          </div>
        ))}
      </div>

      {/* Attention card */}
      <div className="lp-card-dark p-4 mb-8">
        <div className="flex items-start gap-3">
          <AlertTriangle
            size={26}
            strokeWidth={1.8}
            style={{ color: 'var(--lp-body)', marginTop: 2 }}
          />
          <div>
            <p
              className="text-[16px] font-semibold mb-1"
              style={{ color: 'var(--lp-cream)' }}
            >
              {attention.count} Items Need Attention
            </p>
            <p
              className="text-[14px] leading-snug"
              style={{ color: 'var(--lp-body)' }}
            >
              2 need replacing, 1 needs adjustment, and 2 condition checks are due.
            </p>
          </div>
        </div>
      </div>

      {/* Designated exit selector (local React state only) */}
      <div className="mb-3">
        <p
          className="text-[12.5px] font-semibold mb-2"
          style={{ color: 'var(--lp-muted)' }}
        >
          Designated exit
        </p>
        <div className="flex flex-wrap gap-2">
          {EXIT_OPTIONS.map((opt) => {
            const selected = opt === exit;
            return (
              <button
                key={opt}
                onClick={() => setExit(opt)}
                className="lp-badge lp-pressable"
                style={{
                  background: selected
                    ? 'var(--lp-green-badge-bg)'
                    : 'rgba(255,255,255,0.06)',
                  color: selected ? 'var(--lp-green-badge-text)' : 'var(--lp-body)',
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>

      {/* Recent activity (always visible, frontend-only mock demo) */}
      <div className="lp-card p-4 mb-3">
        <p
          className="text-[12.5px] font-semibold mb-2"
          style={{ color: 'var(--lp-muted)' }}
        >
          Recent activity
        </p>
        {ESCAPE_EVENTS.map((e, i) => (
          <p
            key={i}
            className="text-[14px] leading-snug"
            style={{ color: 'var(--lp-body)' }}
          >
            {e.time} —{' '}
            {e.type === 'near_exit' ? `Activity near ${exit.toLowerCase()}` : e.text}
          </p>
        ))}
        {/* Live Ring observations shown as source-neutral activity evidence. */}
        {ringActivity.map((e) => (
          <p
            key={e.id}
            className="text-[14px] leading-snug"
            style={{ color: 'var(--lp-body)' }}
          >
            {e.time} — Activity near {exit.toLowerCase()}
          </p>
        ))}
      </div>

      {/* Pet Escape Early Warning (only the warning depends on escapeAlert) */}
      {escapeAlert && (
        <div className="lp-card-alert p-4 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle
              size={26}
              strokeWidth={1.8}
              style={{ color: 'var(--lp-body)', marginTop: 2 }}
            />
            <div>
              <p
                className="text-[16px] font-semibold mb-1"
                style={{ color: 'var(--lp-cream)' }}
              >
                Possible Escape Attempt
              </p>
              <p
                className="text-[14px] leading-snug mb-3"
                style={{ color: 'var(--lp-body)' }}
              >
                Repeated activity has been detected near the designated
                exit area.
              </p>
              <p
                className="text-[12.5px] font-semibold mb-1"
                style={{ color: 'var(--lp-muted)' }}
              >
                Reason:
              </p>
              <ul className="text-[14px] leading-snug mb-3" style={{ color: 'var(--lp-body)' }}>
                <li>Multiple approaches to exit area</li>
                <li>Activity repeated within a short period</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Today */}
      <h3
        className="font-serif-lp text-[24px] mb-4"
        style={{ color: 'var(--lp-cream)' }}
      >
        Today
      </h3>

      {active && (
        <button
          onClick={() => navigate(`/memory/${active.id}`)}
          className="lp-card w-full text-left p-4 lp-pressable"
        >
          <div className="flex items-center gap-3.5">
            <img
              src={active.thumbnail}
              alt=""
              className="w-[54px] h-[54px] rounded-xl object-cover flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p
                className="text-[16px] font-semibold mb-0.5"
                style={{ color: 'var(--lp-cream)' }}
              >
                {isConfirmed ? active.confirmedTitle : active.provisionalTitle}
              </p>
              <p className="text-[13.5px]" style={{ color: 'var(--lp-body)' }}>
                {active.duration} · {active.source}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              {isConfirmed ? (
                <span className="lp-badge">Confirmed</span>
              ) : (
                <span className="lp-badge">Needs confirmation</span>
              )}
              <span
                className="text-[14px] flex items-center gap-0.5"
                style={{ color: 'var(--lp-cream)' }}
              >
                {isConfirmed ? 'View' : 'Review'}
                <ChevronRight size={15} />
              </span>
            </div>
          </div>
        </button>
      )}

      {/* View memory link */}
      <div className="text-center mt-10">
        <button
          onClick={() => navigate('/memory')}
          className="text-[16px] lp-pressable inline-flex items-center gap-1"
          style={{ color: 'var(--lp-cream)' }}
        >
          View memory →
        </button>
      </div>
    </div>
  );
};

export default Home;
